import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

import httpClient from "@/common/api/httpClient";
import httpMethods from "@/common/api/httpMethods";

export interface ModuleType {
  id: number;
  project_id: number;
  name: string;
  created_at?: string;
  updated_at?: string;
}

export async function fetchModuleTypes(
  projectId?: number | string | null,
): Promise<ModuleType[]> {
  const url = projectId
    ? `/projects/${projectId}/module-types`
    : "/module_types";
  try {
    const res = await httpClient<ModuleType[]>(httpMethods.GET, url);
    const raw = (res as any)?.data ?? res;
    if (Array.isArray(raw)) return raw;
    if (Array.isArray(raw?.data)) return raw.data;
    if (Array.isArray(raw?.content)) return raw.content;
    return [];
  } catch (err) {
    console.error("Failed to fetch module types:", err);
    return [];
  }
}

export async function createModuleType(
  projectId: number | string,
  name: string,
): Promise<ModuleType> {
  const res = await httpClient<ModuleType>(
    httpMethods.POST,
    `/projects/${projectId}/module-types`,
    { projectId, name },
  );
  const raw = (res as any)?.data ?? res;
  return raw?.data ?? raw;
}

export async function deleteModuleType({
  projectId,
  id,
}: {
  projectId: number | string;
  id: number | string;
}): Promise<void> {
  await httpClient(
    httpMethods.DELETE,
    `/projects/${projectId}/module-types/${id}`,
  );
}

export function useModuleTypes(projectId?: number | string | null) {
  const pKey = projectId != null ? String(projectId) : "all";
  return useQuery({
    queryKey: ["moduleTypes", pKey],
    queryFn: () => fetchModuleTypes(projectId),
    enabled: true,
    staleTime: 5 * 60 * 1000,
  });
}

export function useCreateModuleType() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      projectId,
      name,
    }: {
      projectId: number | string;
      name: string;
    }) => createModuleType(projectId, name),
    onSuccess: (_, { projectId }) => {
      queryClient.invalidateQueries({ queryKey: ["moduleTypes"] });
    },
  });
}

export function useUpdateModuleType() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      projectId,
      id,
      name,
    }: {
      projectId: number | string;
      id: number | string;
      name: string;
    }) =>
      httpClient<ModuleType>(
        httpMethods.PUT,
        `/projects/${projectId}/module-types/${id}`,
        {
          name,
        },
      ).then((res) => {
        const raw = (res as any)?.data ?? res;
        return (raw?.data ?? raw) as ModuleType;
      }),
    onSuccess: (_, { projectId }) => {
      queryClient.invalidateQueries({ queryKey: ["moduleTypes"] });
    },
  });
}

export function useDeleteModuleType() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteModuleType,
    onSuccess: (_, { projectId }) => {
      queryClient.invalidateQueries({ queryKey: ["moduleTypes"] });
      queryClient.invalidateQueries({ queryKey: ["testCases"] });
      queryClient.invalidateQueries({ queryKey: ["defects"] });
      queryClient.invalidateQueries({ queryKey: ["requirements"] });
    },
  });
}
